import React, { type FunctionComponent, useState, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { type PartnerCallbackConfiguration } from 'domains/connect/api'
import { useFormErrorMessageMap } from 'utils/react-hook-form/reactHookForm.hooks'
import { Input } from 'gin-and-tonic/common/input/Input'
import { Button } from 'gin-and-tonic/common/button/Button'
import { Select } from 'legacy/dash-components/select/Select'
import { type SimpleOption } from 'legacy/dash-blocks/dash-select/utils/select.types'
import { useCallbackConfigurationForm } from 'domains/connect/components/utils/callbackConfigurationForm.hooks'
import {
  containerStyles,
  fieldsetStyle,
  legendStyle,
  fieldRowStyle,
  inputLabelStyle,
  checkboxContainerStyle,
  checkboxItemStyle,
  formStyle,
} from 'domains/connect/components/styles'

interface ContainerProps {
  callbackConfigurations: PartnerCallbackConfiguration[]
}

export const CallbackConfigurationFormContainer: FunctionComponent<ContainerProps> = ({
  callbackConfigurations,
}) => {
  const [environment, setEnvironment] = useState(callbackConfigurations[0].environment)
  function findCallbackConfiguration() {
    const conf = callbackConfigurations.find(e => e.environment === environment)
    if (conf) {
      return conf
    }
    throw new Error('could not find environment')
  }
  return (
    <React.Fragment>
      <div css={containerStyles}>
        <fieldset css={fieldsetStyle}>
          <legend css={legendStyle}>
            <FormattedMessage id="connect.configuration.environment" />
          </legend>
          <Select<SimpleOption<string>>
            value={{ label: environment, value: environment }}
            options={callbackConfigurations.map(cc => ({
              label: cc.environment,
              value: cc.environment,
            }))}
            onChange={({ value }) => setEnvironment(value)}
          ></Select>
        </fieldset>
        <CallbackConfigurationForm
          callbackConfiguration={findCallbackConfiguration()}
        ></CallbackConfigurationForm>
      </div>
    </React.Fragment>
  )
}

interface Props {
  callbackConfiguration: PartnerCallbackConfiguration
}

export const CallbackConfigurationForm: FunctionComponent<Props> = ({ callbackConfiguration }) => {
  const { onSubmit, register, setValue, errors } =
    useCallbackConfigurationForm(callbackConfiguration)

  useEffect(() => {
    if (callbackConfiguration && callbackConfiguration.availableStatuses) {
      setValue('authUri', callbackConfiguration.authUri)
      setValue('callbackUri', callbackConfiguration.callbackUri)
      setValue('accessTokenTtl', callbackConfiguration.accessTokenTtl)
      setValue('clientId', callbackConfiguration.credentialsClientId)
      setValue('clientSecret', callbackConfiguration.credentialsClientSecret)

      callbackConfiguration.availableStatuses.forEach(status => {
        if (callbackConfiguration.statusesOfInterest.includes(status)) {
          setValue(status, 'checked')
        } else {
          setValue(status, false)
        }
      })
    }
  }, [callbackConfiguration, setValue])

  const authUriFieldRef = register({ required: true })
  const callbackUriFieldRef = register({ required: true })
  const clientIdFieldRef = register({ required: true })
  const clientSecretFieldRef = register({ required: true })
  const accessTokenTtlRef = register({ required: true })

  const authUriErrorMessageId = useFormErrorMessageMap({
    required: 'forms.validations.required',
  })(errors.authUri)
  const callbackUriErrorMessageId = useFormErrorMessageMap({
    required: 'forms.validations.required',
  })(errors.callbackUri)
  const clientIdErrorMessageId = useFormErrorMessageMap({
    required: 'forms.validations.required',
  })(errors.clientId)
  const clientSecretErrorMessageId = useFormErrorMessageMap({
    required: 'forms.validations.required',
  })(errors.clientSecret)
  const accessTokenTtlErrorMessageId = useFormErrorMessageMap({
    required: 'forms.validations.required',
  })(errors.accessTokenTtl)

  return (
    <form css={formStyle} onSubmit={onSubmit}>
      <fieldset css={fieldsetStyle}>
        <legend css={legendStyle}>
          <FormattedMessage id="connect.configuration.applicationConfiguration" />
        </legend>

        <div css={fieldRowStyle}>
          <label htmlFor="clientId" css={inputLabelStyle}>
            <FormattedMessage id="connect.configuration.form.fields.clientId" />:
          </label>
          <Input name="clientId" errorMessageId={clientIdErrorMessageId} ref={clientIdFieldRef} />
        </div>

        <div css={fieldRowStyle}>
          <label htmlFor="clientSecret" css={inputLabelStyle}>
            <FormattedMessage id="connect.configuration.form.fields.clientSecret" />:
          </label>
          <Input
            name="clientSecret"
            errorMessageId={clientSecretErrorMessageId}
            ref={clientSecretFieldRef}
          />
        </div>

        <div css={fieldRowStyle}>
          <label htmlFor="authUri" css={inputLabelStyle}>
            <FormattedMessage id="connect.configuration.form.fields.authUri" />:
          </label>
          <Input name="authUri" errorMessageId={authUriErrorMessageId} ref={authUriFieldRef} />
        </div>

        <div css={fieldRowStyle}>
          <label htmlFor="callbackUri" css={inputLabelStyle}>
            <FormattedMessage id="connect.configuration.form.fields.callbackUri" />:
          </label>
          <Input
            name="callbackUri"
            errorMessageId={callbackUriErrorMessageId}
            ref={callbackUriFieldRef}
          />
        </div>

        <div css={fieldRowStyle}>
          <label htmlFor="accessTokenTtl" css={inputLabelStyle}>
            <FormattedMessage id="connect.configuration.form.fields.accessTokenTtl" />:
          </label>
          <Input
            name="accessTokenTtl"
            errorMessageId={accessTokenTtlErrorMessageId}
            ref={accessTokenTtlRef}
          />
        </div>
      </fieldset>

      <fieldset css={fieldsetStyle}>
        <legend css={legendStyle}>
          <FormattedMessage id="connect.configuration.callbackSettings" />
        </legend>
        <div css={checkboxContainerStyle}>
          {callbackConfiguration.availableStatuses.map((status: string, index: number) => (
            <div key={`check-${index}`} css={checkboxItemStyle}>
              <label htmlFor={status}>{status}</label>
              <input type="checkbox" name={status} ref={register} />
            </div>
          ))}
        </div>
      </fieldset>
      <Button type="submit" messageId="connect.configuration.form.submit" />
    </form>
  )
}
